import { useNavigate } from "react-router"
import {
  Field,
  CustomSelectField,
  FormCard,
  FormError,
  PageHero,
  SubmitButton,
  TextAreaField,
  usePublicForm,
} from "./components"
import { OnlineLearningIcon } from "./icons"
import { DonateSection } from "./donate-section"

function ShieldTickIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
      <path d="M12 3.5 18.5 6v5.5c0 4.1-2.7 7.6-6.5 9-3.8-1.4-6.5-4.9-6.5-9V6L12 3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="m8.5 12 2.2 2.2 4.8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
      <path d="M12 20.4S5 16.3 3.3 12.2c-1.1-2.6.2-5.4 2.9-6.1 2.2-.6 4 .7 5.1 2.2 1.1-1.5 2.9-2.8 5.1-2.2 2.7.7 4 3.5 2.9 6.1C19 16.3 12 20.4 12 20.4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const BENEFITS = [
  {
    title: "What sponsorship covers",
    body: "Tuition, books, uniform, exam fees and learning materials — end to end, for a full academic year.",
    icon: <OnlineLearningIcon className="h-6 w-6" />,
  },
  {
    title: "Direct & transparent",
    body: "Funds go straight to the partner school. You get a signed statement each term.",
    icon: <ShieldTickIcon />,
  },
  {
    title: "A real connection",
    body: "Photos, report cards and progress notes from your child's classroom every term.",
    icon: <HeartIcon />,
  },
]

const SPONSORSHIP_TYPES = ["Child sponsorship", "Women's empowerment", "Ability Program"] as const
const INTERESTS = [
  "One child, one term",
  "One child, one year",
  "One child, multi-year",
  "Multiple children",
  "Corporate / organization sponsorship",
  "One-off donation",
] as const

export function SponsorPage() {
  const navigate = useNavigate()
  const { status, submit } = usePublicForm("/api/public/inquiries")

  return (
    <>
      <PageHero
        className="sponsor-hero"
        eyebrow={
          <>
            <span className="sponsor-eyebrow-desktop">Sponsor with Redempta</span>
            <span className="sponsor-eyebrow-mobile">Sponsor a child</span>
          </>
        }
        title={<>Fund a future. <span className="text-forest-700">Change a life.</span></>}
        lede={<>Sponsor a child through school, a woman building a business, or a person living with disability gaining access to devices, therapy, and education. <span className="text-forest-700">You choose the path; we handle the details and send real updates.</span></>}
        eyebrowDot
      />

      <DonateSection />

      <section className="sponsor-section bg-cream-50 py-16 lg:pb-[100px] lg:pt-[60px]">
        <div className="public-container grid items-start gap-10 lg:grid-cols-[550px_751px] lg:justify-between lg:gap-0">
          <div className="sponsor-benefits">
            {BENEFITS.map((benefit) => (
              <article key={benefit.title} className="sponsor-benefit">
                <span className="sponsor-benefit-icon">{benefit.icon}</span>
                <h3>{benefit.title}</h3>
                <p>{benefit.body}</p>
              </article>
            ))}
          </div>

          <FormCard
            className="sponsor-form-card"
            title="Start your sponsorship"
            subtitle="Share a few details and our team will reach out to walk you through matching, funding, and updates."
          >
            <form
              onSubmit={(event) => {
                const form = new FormData(event.currentTarget)
                const email = String(form.get("email") ?? "")
                const sponsorshipType = String(form.get("sponsorshipType") ?? "")
                const interest = String(form.get("interest") ?? "")
                const notes = String(form.get("notes") ?? "")
                const message = [
                  `Sponsorship type: ${sponsorshipType}`,
                  `Interest: ${interest}`,
                  notes ? `\n${notes}` : "",
                ].join("\n")
                void submit(event, {
                  name: String(form.get("name") ?? ""),
                  email,
                  phone: String(form.get("phone") ?? "") || undefined,
                  type: "sponsorship",
                  message,
                }).then((submitted) => {
                  if (submitted) {
                    navigate("/donor-signin", {
                      state: { email, autoRequestOtp: true },
                    })
                  }
                })
              }}
              className="sponsor-form"
            >
              <Field label="Full name" name="name" required placeholder="Your name" disabled={status === "loading"} />
              <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:gap-4">
                <Field label="Email" name="email" required type="email" placeholder="you@example.com" disabled={status === "loading"} />
                <Field label="Phone Number" name="phone" required type="tel" placeholder="+234..." disabled={status === "loading"} />
              </div>
              <CustomSelectField label="Sponsorship type" name="sponsorshipType" options={SPONSORSHIP_TYPES} disabled={status === "loading"} />
              <CustomSelectField label="Sponsorship interest" name="interest" options={INTERESTS} disabled={status === "loading"} />
              <TextAreaField label="Anything we should know?" name="notes" placeholder="Optional..." disabled={status === "loading"} />
              <div className="pt-2">
                <SubmitButton loading={status === "loading"} withArrow={false}>Submit inquiry</SubmitButton>
                <p className="mt-4 text-sm text-ink/60">
                  We treat every inquiry as a formal commitment to explore sponsorship, not a mailing list signup.
                </p>
                {status === "error" && <FormError />}
              </div>
            </form>
          </FormCard>
        </div>
      </section>
    </>
  )
}
